import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import NewSectionTitle from '@components/shared/NewSectionTitle';
import quateIcon from '@assets/placementpage-images/quate.svg';
import testimonialOne from '@assets/placementpage-images/testimonialone.png';
import testimonialTwo from '@assets/placementpage-images/testimonialtwo.png';
import Lines from '@assets/placementpage-images/lines.svg';
import useMediaQuery from '@utils/hooks/useMediaQuery';

const Testimonials = ({ placementPage, recruiterTestimonials }) => {
  const [expandedCard, setExpandedCard] = useState(0);
  const [animatedText, setAnimatedText] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const isMobile = useMediaQuery('(max-width: 767.98px)');

  const recruiterTestimonialsData = recruiterTestimonials.map((item, index) => ({
    id: item?.id,
    name: item?.Name,
    review: item?.Review,
    position: item?.Designation,
    image: item?.Image,
    color:  'bg-[linear-gradient(180deg,#F00037,transparent)]',
  }));

  const partners = [
    {
      name: 'Ritika Sharma',
      position: 'Data Scientist, Google',
      image: testimonialOne,
      color: 'bg-[#f00037]',
    },
    {
      name: 'Rahul Mehta',
      position: 'Software Engineer, Microsoft',
      image: testimonialOne,
      color: 'bg-[#f00037]',
    },
    {
      name: 'Priya Desai',
      position: 'Product Manager, Amazon',
      image: testimonialOne,
      color: 'bg-[#f00037]',
    },
    {
      name: 'Neha Gupta',
      position: 'UX Designer, Apple',
      image: testimonialOne,
      color: 'bg-[#f00037]',
    },
  ];

  const animateText = (text) => {
    if (!text) return;
    setIsAnimating(true);
    setAnimatedText('');
    const words = text.split(' ');
    let currentIndex = 0;

    const interval = setInterval(() => {
      if (currentIndex < words.length) {
        setAnimatedText(prev => prev + (currentIndex === 0 ? '' : ' ') + words[currentIndex]);
        currentIndex++;
      } else {
        clearInterval(interval);
        setIsAnimating(false);
      }
    }, 100); // Adjust speed as needed

    return () => clearInterval(interval);
  };

  // Initialize animation for first testimonial
  useEffect(() => {
    if (recruiterTestimonialsData?.[0]?.review) {
      animateText(recruiterTestimonialsData[0].review);
    }
  }, []);

  // Animate text when expandedCard changes
  useEffect(() => {
    if (recruiterTestimonialsData?.[expandedCard]?.review) {
      animateText(recruiterTestimonialsData[expandedCard].review);
    }
  }, [expandedCard]);

  return (
    <div
      className="flex w-full flex-col px-6 py-12 md:flex-row"
      style={{ backgroundColor: '#fff' }}
    >
      {/* Left column - Testimonial text */}
      <div className="relative right-[25px] flex w-full flex-col items-start p-6 pr-12 md:right-0 md:p-10">
        <NewSectionTitle
          text="Here's What Our Hiring Partners"
          highlightedText="Say"
          placementPage={placementPage}
          testimonial={true}
        />

        <div className="mt-10 mb-6 hidden md:block">
          <Image src={quateIcon} width={44} height={44} alt="double quate icon" />

          <p className="mt-5 max-w-[400px] break-words font-proxima-nova text-[18px] font-normal italic leading-[27px] text-[#1F1F1F]">
            {isAnimating ? animatedText : recruiterTestimonialsData[expandedCard]?.review}
          </p>
        </div>

        <div className="z-10 mt-12 hidden cursor-pointer items-center md:flex">
          <button
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-[#DADADA] text-[#DADADA] hover:border-[#f00037] hover:text-[#f00037]"
            onClick={() =>
              setExpandedCard((prev) =>
                prev === 0 ? recruiterTestimonialsData.length - 1 : prev - 1
              )
            }
          >
            &lt;
          </button>
          <button
            className="ml-2 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-[#DADADA] text-[#DADADA] hover:border-[#f00037] hover:text-[#f00037]"
            onClick={() =>
              setExpandedCard((prev) =>
                prev === recruiterTestimonialsData.length - 1 ? 0 : prev + 1
              )
            }
          >
            &gt;
          </button>
        </div>

        {/* Lines image positioned at bottom left */}
        <div className="absolute top-[346px] left-[-63px] z-[1] hidden h-[299px] w-[215px] md:block">
          <Image src={Lines} width={215} height={299} alt="cross-lines" />
        </div>
      </div>
      {/* Right column - Cards */}
      <div className="mr-20 flex w-full justify-between md:w-2/3 md:justify-end">
        <div className="relative">
          {/* Testimonial cards container */}
          <div className="space-x relative flex h-[300px] w-[400px] flex-row justify-between space-x-2 md:h-[554px] md:w-full md:justify-end">
            {recruiterTestimonialsData?.map((partner, index) => (
              <div
                key={index}
                className={`relative cursor-pointer overflow-hidden rounded-xl transition-all duration-1000 ease-in-out ${
                  index === expandedCard ? 'w-64' : 'w-16 md:w-16 lg:w-24 grayscale'
                }`}
                style={{ height: isMobile ? '300px' : '554px' }}
                onMouseEnter={() => setExpandedCard(index)}
              >
                <div
                  className={`absolute inset-0 ${
                    index === expandedCard ? partner.color : 'bg-[#EFEFEF]'
                  } transition-colors duration-500`}
                ></div>

                <div
                  className={`absolute top-6 right-6 z-10 text-right  transition-opacity duration-300 ${
                    index === expandedCard ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <h3 className="text-[20px] font-medium text-white md:text-2xl">{partner.name}</h3>
                  <p className="text-[12px] text-white/80 md:text-sm">{partner.position}</p>
                </div>

                {/* Image Container */}
                <div className="absolute bottom-0 right-0 h-3/4 md:h-4/5 w-full overflow-hidden">
                  <div className="relative flex h-full w-full items-end justify-end">
                    <div
                      className="relative ml-auto transition-all duration-500"
                      style={{
                        width: index === expandedCard 
                          ? (isMobile ? '250px' : '320px') 
                          : (isMobile ? '177px' : '320px'),
                        height: index === expandedCard 
                          ? (isMobile ? '200px' : '480px') 
                          : (isMobile ? '100%' : '650px'),
                        marginRight: index === expandedCard 
                          ? (isMobile ? '-20px' : '-80px') 
                          : (isMobile ? '-10px' : '-40px')
                      }}
                    >
                      <Image
                        src={partner.image}
                        alt="hiring partners"
                        layout="fill"
                        width={isMobile ? 250 : 280}
                        height={isMobile ? 200 : 410}
                        objectFit="contain"
                        objectPosition="bottom right"
                        className={`transition-opacity duration-500 ${
                          index === expandedCard ? 'opacity-100' : 'opacity-70'
                        }`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer text - aligned with cards */}
          <div className="absolute -bottom-8 right-0 mr-44 flex items-center gap-1 md:-bottom-12 md:mr-10">
            <span className="font-proxima-nova text-[20px] font-semibold leading-[140%] tracking-[-0.64px] text-[#f00037] md:text-[28px]">
              1200 +
            </span>
            <span className="text-center font-proxima-nova text-[20px] font-semibold leading-[140%] tracking-[-0.64px] text-[#A9A9A9] md:text-[32px]">
              Hiring Partners
            </span>
          </div>
        </div>
      </div>
      <div className="mt-14 mb-6 block md:hidden">
        <Image src={quateIcon} width={44} height={44} alt="double quate icon" />

        <p className="mt-5 font-proxima-nova text-[18px] font-normal italic leading-[27px] text-[#1F1F1F]">
          {isAnimating ? animatedText : recruiterTestimonialsData[expandedCard]?.review}
        </p>
      </div>

      <div className="mt-12  flex cursor-pointer items-center md:hidden">
        <button
          className="flex h-10 w-10 items-center justify-center rounded-full border border-[#DADADA] text-[#DADADA] hover:border-[#f00037] hover:text-[#f00037]"
          onClick={() =>
            setExpandedCard((prev) =>
              prev === 0 ? recruiterTestimonialsData.length - 1 : prev - 1
            )
          }
        >
          &lt;
        </button>
        <button
          className="ml-2 flex h-10 w-10 items-center justify-center rounded-full border border-[#DADADA] text-[#DADADA] hover:border-[#f00037] hover:text-[#f00037]"
          onClick={() =>
            setExpandedCard((prev) =>
              prev === recruiterTestimonialsData.length - 1 ? 0 : prev + 1
            )
          }
        >
          &gt;
        </button>
      </div>
    </div>
  );
};

export default Testimonials;
